// src/pages/PlanDesarrollo.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FileText, RefreshCw } from "lucide-react";
import { apiConfig } from "../config/api.config";

const COLORS = {
  info: "#3b82f6",
  purple: "#8b5cf6",
  danger: "#ef4444",
  gray: "#6b7280",
};

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatMoneyCO(v) {
  const n = toNum(v);
  return new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(n);
}

function formatPctCO(v) {
  const n = toNum(v);
  return `${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 1 }).format(n)}%`;
}

// Orden fijo por código: 1.x.x -> 2.x.x -> ... -> 5.x.x (no por valor)
function parseProgramaCode(code) {
  const s = String(code || "").trim();
  if (!s) return { a: 99, b: 99, c: 99, raw: "" };

  const parts = s.split(".").map((x) => parseInt(x, 10));
  const a = Number.isFinite(parts[0]) ? parts[0] : 99;
  const b = Number.isFinite(parts[1]) ? parts[1] : 99;
  const c = Number.isFinite(parts[2]) ? parts[2] : 99;

  return { a, b, c, raw: s };
}

function comparePrograma(a, b) {
  const pa = parseProgramaCode(a);
  const pb = parseProgramaCode(b);
  if (pa.a !== pb.a) return pa.a - pb.a;
  if (pa.b !== pb.b) return pa.b - pb.b;
  if (pa.c !== pb.c) return pa.c - pb.c;
  return pa.raw.localeCompare(pb.raw);
}

export default function PlanDesarrollo() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [filtroFecha, setFiltroFecha] = useState("todo");
  const [metrica, setMetrica] = useState("presupuesto_distribuido"); // se deja por UI (no afecta tabla)

  const cargar = useCallback(async () => {
    try {
      setErr("");
      setLoading(true);

      const cacheBust = `t=${Date.now()}`;
      const url = apiConfig.getUrl(
        "/api/plan_desarrollo.php",
        `filtro=${encodeURIComponent(filtroFecha)}&${cacheBust}`,
      );

      const resp = await fetch(url, { credentials: "include" });
      const json = await resp.json().catch(() => ({}));

      if (!resp.ok || !json?.ok) {
        throw new Error(json?.error || json?.message || `Error ${resp.status}`);
      }

      setData(json.data || null);
    } catch (e) {
      setData(null);
      setErr(e?.message || "No se pudo cargar Plan de Desarrollo");
    } finally {
      setLoading(false);
    }
  }, [filtroFecha]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // ✅ polling cada 10 minutos
  useEffect(() => {
    const id = setInterval(() => {
      cargar();
    }, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, [cargar]);

  // =========================
  // PROGRAMAS (normalizados)
  // =========================
  const programas = useMemo(() => {
    const rows = Array.isArray(data?.programas)
      ? data.programas
      : Array.isArray(data?.rows)
        ? data.rows
        : [];

    const acc = new Map();

    const add = (codigo, baseRow) => {
  const key = String(codigo || "").trim();
  if (!key) return;

  const prev = acc.get(key) || {
    programa: key,
    count: 0,
    presupuesto_aprobado: 0, // ✅ un solo campo
  };

  // ✅ tu backend ahora manda "necesidades_aprobadas"
  prev.count += toNum(
    baseRow?.necesidades_aprobadas ??
      baseRow?.count_aprobados ??
      baseRow?.count ??
      baseRow?.cantidad ??
      baseRow?.conteo ??
      0,
  );

  // ✅ presupuesto aprobado (tu backend manda "presupuesto_aprobado")
  prev.presupuesto_aprobado += toNum(
    baseRow?.presupuesto_aprobado ??
      baseRow?.presupuesto ??
      baseRow?.total ??
      0,
  );

  acc.set(key, prev);
};

    for (const r of rows) {
      const raw = String(r?.programa ?? r?.codigo ?? r?.code ?? "").trim();
      if (!raw) continue;

      const codes = raw
        .split(/[,;\n]+/g)
        .map((x) => x.trim())
        .filter(Boolean);

      if (!codes.length) continue;

      for (const c of codes) add(c, r);
    }

    const out = Array.from(acc.values());
    out.sort((x, y) => comparePrograma(x.programa, y.programa));
    return out;
  }, [data]);

  // ✅ Tabla SOLO programas reales (sin SIN_PROGRAMA)
  // IMPORTANTE: esto NO es hook después del return; va acá arriba.
  const programasTabla = useMemo(() => {
    return programas.filter((p) => p.programa !== "SIN_PROGRAMA");
  }, [programas]);

  // =========================
  // KPIs (con SIN_PROGRAMA real)
  // =========================
 const kpis = useMemo(() => {
  const k = data?.kpis || {};

  
  const totalAprobados = toNum(k.total_aprobados);
  const funcionamientoCount = toNum(k.funcionamiento);
  const valorFuncionamiento = toNum(k.valor_funcionamiento);

  const inversionCount = toNum(k.inversion); // 👈 NO lo calcules, ya viene correcto
  const valorTotal = toNum(k.presupuesto_total_aprobado);
  const valorInversion = toNum(k.presupuesto_total_con_programa);

  return {
    total_aprobados: totalAprobados,
    valor_total: valorTotal,

    funcionamiento_count: funcionamientoCount,
    valor_funcionamiento: valorFuncionamiento,

    inversion_count: inversionCount,
    valor_inversion: valorInversion,
  };
}, [data]);

  // denom para %: se calcula sobre el valor con programa (inversión)
const denomPct = toNum(kpis.valor_total);

  if (loading) {
    return (
      <div style={{ display: "flex", minHeight: "60vh", alignItems: "center", justifyContent: "center" }}>
        <div
          style={{
            width: 48,
            height: 48,
            border: "4px solid #e5e7eb",
            borderTop: "4px solid #3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "1.5rem",
        maxWidth: "1400px",
        margin: "0 auto",
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "10px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          padding: "1.5rem",
          marginBottom: "1.5rem",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <div>
            <h1 style={{ fontSize: "1.7rem", fontWeight: 900, color: "#111827", margin: 0 }}>
              📌 Plan de Desarrollo – Programas
            </h1>
            <p style={{ color: "#6b7280", margin: "0.35rem 0 0 0", fontSize: "1rem", fontWeight: 700 }}>
              Actualiza automáticamente cada 10 minutos.
            </p>
          </div>

          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
            <select
              value={filtroFecha}
              onChange={(e) => setFiltroFecha(e.target.value)}
              style={{
                padding: "0.6rem 1rem",
                border: "1px solid #d1d5db",
                borderRadius: "10px",
                fontSize: "1rem",
                cursor: "pointer",
                fontWeight: 800,
              }}
            >
              <option value="todo">Todo el tiempo</option>
              <option value="30dias">Últimos 30 días</option>
              <option value="90dias">Últimos 90 días</option>
              <option value="año">Último año</option>
            </select>

            {/* se deja para no cambiar tu UI */}
            <select
              value={metrica}
              onChange={(e) => setMetrica(e.target.value)}
              style={{
                padding: "0.6rem 1rem",
                border: "1px solid #d1d5db",
                borderRadius: "10px",
                fontSize: "1rem",
                cursor: "pointer",
                fontWeight: 800,
              }}
            >
              <option value="presupuesto_distribuido">Presupuesto (distribuido)</option>
              <option value="presupuesto_sin_distribuir">Presupuesto (sin distribuir)</option>
              <option value="count">Conteo aprobados</option>
            </select>

            <button
              onClick={cargar}
              style={{
                padding: "0.6rem 1rem",
                backgroundColor: "#8b5cf6",
                color: "white",
                border: "none",
                borderRadius: "10px",
                fontSize: "1rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontWeight: 800,
              }}
            >
              <RefreshCw size={18} />
              Refrescar
            </button>
          </div>
        </div>

        {err && (
          <div style={{ marginTop: "0.9rem", color: COLORS.danger, fontWeight: 900 }}>
            {err}
          </div>
        )}
      </div>

      {/* KPIs */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <KpiMulti
          title="Total aprobados"
          color={COLORS.info}
          lines={[
            { label: "Total", value: `${kpis.total_aprobados}` },
            { label: "Inversión", value: `${kpis.inversion_count}` },
            { label: "Funcionamiento", value: `${kpis.funcionamiento_count}` },
          ]}
        />

        <Kpi title="Presupuesto aprobado total" value={`$ ${formatMoneyCO(kpis.valor_total)}`} color={COLORS.info} />

        <Kpi title="Presupuesto con programa" value={`$ ${formatMoneyCO(kpis.valor_inversion)}`} color={COLORS.purple} />

        <Kpi title="Valor Funcionamiento" value={`$ ${formatMoneyCO(kpis.valor_funcionamiento)}`} color={COLORS.purple} />
      </div>

      {/* Tabla */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "10px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          padding: "1.5rem",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>
          <div style={{ fontWeight: 900, fontSize: "1.2rem", color: "#111827" }}>
            Resumen de contratación (status = aprobado) por programa del Plan de Desarrollo
          </div>
          <div style={{ color: COLORS.gray, fontWeight: 800 }}>
            Programas: {programasTabla.length}
          </div>
        </div>

        {programasTabla.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem", color: "#6b7280", fontWeight: 800 }}>
            <FileText size={42} style={{ marginBottom: "0.5rem" }} />
            <div>No hay datos para mostrar.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "0.95rem",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f3f4f6" }}>
                  <Th>Código programa</Th>
                  <Th>Necesidades aprobadas</Th>
                  <Th>Presupuesto aprobado</Th>
                  <Th>% del total</Th>
                </tr>
              </thead>

              <tbody>
                {programasTabla.map((r, idx) => {
                  const pct = denomPct > 0 ? (toNum(r.presupuesto_aprobado) * 100) / denomPct : 0;

                  return (
                    <tr
                      key={`${r.programa}-${idx}`}
                      style={{ backgroundColor: idx % 2 === 0 ? "white" : "#f9fafb" }}
                    >
                      <Td strong>{r.programa}</Td>
                      <Td strong>{toNum(r.count)}</Td>
                      <Td strong>$ {formatMoneyCO(r.presupuesto_aprobado)}</Td>
                      <Td strong>{formatPctCO(pct)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: "0.9rem", color: COLORS.gray, fontWeight: 800, fontSize: "0.95rem" }}>
        </div>
      </div>
    </div>
  );
}

function Kpi({ title, value, color }) {
  return (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "10px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        padding: "1.25rem",
      }}
    >
      <div style={{ fontSize: "1rem", color: "#6b7280", fontWeight: 800 }}>{title}</div>
      <div style={{ fontSize: "2rem", fontWeight: 900, color, marginTop: "0.4rem" }}>{value}</div>
    </div>
  );
}

function KpiMulti({ title, lines, color }) {
  return (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "10px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        padding: "1.25rem",
      }}
    >
      <div style={{ fontSize: "1rem", color: "#6b7280", fontWeight: 800 }}>{title}</div>

      <div style={{ marginTop: "0.6rem", display: "grid", gap: "0.35rem" }}>
        {lines.map((l, idx) => (
          <div key={idx} style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem" }}>
            <div style={{ color: "#6b7280", fontWeight: 800 }}>{l.label}</div>
            <div style={{ fontWeight: 900, color }}>{l.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Th({ children }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "10px",
        borderBottom: "1px solid #e5e7eb",
        fontWeight: 900,
        whiteSpace: "nowrap",
        fontSize: "0.95rem",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, strong = false }) {
  return (
    <td
      style={{
        padding: "10px",
        borderBottom: "1px solid #e5e7eb",
        fontWeight: strong ? 900 : 800,
        whiteSpace: "nowrap",
        verticalAlign: "top",
      }}
    >
      {children}
    </td>
  );
}
